// src/hooks/usePremiumCopy.js
// Preia textele variabile pentru pagina /premium din backend (/api/config/premium-copy).
// Cache-uiesc in localStorage ~60s ca ecranul sa se randeze instant la revenire.
// Daca nu avem retea deloc, cad pe defaults hardcodate (copy identic cu seed-ul din backend).

import { useEffect, useState } from 'react';
import {
  Star,
  Heart,
  GraduationCap,
  MessageCircle,
  Globe,
  Check,
  Sparkles,
  Leaf,
  Sun,
  Moon,
  Music,
  BookOpen,
  Shield,
  Award,
  Smile,
} from 'lucide-react';
import { getApiBaseUrl } from '../utils/apiConfig';

const CACHE_KEY = 'nv_premium_copy_v3'; // v3: deep merge trateaza "" ca absent (cade pe default)
const CACHE_TTL_MS = 60_000; // 60s — suficient ca salvarile din admin sa se reflecte repede

// Defaults identici cu cei din nativus-backend/routes/premiumCopy.js (DEFAULT_COPY)
// si cu seed-ul din nativus-backend/migrations/008_premium_copy.sql.
// Tineti cele 3 locuri sincronizate cand schimbati textele oficiale.
export const DEFAULT_PREMIUM_COPY = {
  hero: {
    title: 'Aici începe transformarea',
    subtitle: 'Descoperă tot ce am pregătit pentru tine.',
  },
  hook: {
    // Card mic afisat pe Home si Descopera (PremiumOfferCard).
    title: 'Obține și mai multă susținere',
    // Template cu placeholder {discount} — procent real injectat la runtime
    // (RevenueCat pe native, REACT_APP_WEB_PRICE_* pe web). Vezi useIntroOffer.
    subtitle_template: 'Accesează oferta de lansare cu {discount}% reducere',
    fallback_subtitle: 'Vezi planurile Premium',
    enabled: true,
    // Legacy — fallback ultim. PremiumOfferCard prefera subtitle_template.
    subtitle: 'Accesează oferta de lansare cu 50% reducere',
  },
  benefits: [
    { icon: 'star',          text: 'Acces complet la tot conținutul.' },
    { icon: 'heart',         text: 'Practici care îți pot schimba viața: te eliberezi de stres, îți crești încrederea în tine și îți regăsești liniștea.' },
    { icon: 'graduationCap', text: 'Ești însoțit doar de psihologi și psihoterapeuți care înțeleg în profunzime prin ce treci.' },
    { icon: 'messageCircle', text: 'Voci reale, umane: fiecare cuvânt e rostit cu empatie și blândețe.' },
    { icon: 'globe',         text: 'Te conectezi mai ușor cu tine, deoarece totul este în limba ta; limba în care emoțiile se simt cu adevărat.' },
  ],
  plans: {
    annual_label: 'Anual',
    monthly_label: 'Lunar',
    recommended_badge: 'Recomandat',
    badge_bottom_left: 'Ofertă de lansare',
    discount_suffix: 'Reducere',
  },
  cta: {
    // Buton CTA principal — folosit si in popup-urile premium (favoritepopuppremium,
    // PremiumPopup) ca sa fie editabil dintr-un singur loc din admin.
    button: 'Activează premium',
    button_loading: 'Se procesează...',
  },
  details: {
    // {price} este inlocuit la runtime cu pretul de baza (DUPA oferta de
    // lansare), formatat in moneda magazinului user-ului (RON/USD/EUR etc.).
    // Functioneaza atat pentru lunar cat si pentru anual.
    monthly: 'Preț de lansare în primele 6 luni, apoi {price} pe lună. Poți anula oricând.',
    annual: 'Preț de lansare în primul an, apoi {price} pe an. Poți anula oricând.',
  },
  toggle: {
    sheet_title: 'Atât costă mai mult timp pentru tine.',
    expand_label: 'Alege planul',
    collapse_label: 'Mai puțin',
  },
  errors: {
    offerings_unavailable: 'Oferta nu poate fi încărcată momentan.',
    retry_button: 'Reîncearcă',
  },
};

// Mapare slug -> componenta lucide pentru beneficii
const ICON_MAP = {
  star: Star,
  heart: Heart,
  graduationCap: GraduationCap,
  messageCircle: MessageCircle,
  globe: Globe,
  check: Check,
  sparkles: Sparkles,
  leaf: Leaf,
  sun: Sun,
  moon: Moon,
  music: Music,
  bookOpen: BookOpen,
  shield: Shield,
  award: Award,
  smile: Smile,
};

export function resolvePremiumIcon(slug) {
  return ICON_MAP[slug] || Star;
}

// Deep merge defensiv: garanteaza ca orice cheie absenta SAU goala ("") din
// raspunsul backend-ului cade pe default. String-urile goale sunt tratate ca
// "lipsa cheie" — altfel un field necompletat in admin ar suprascrie default-ul
// cu gol si UI-ul ar ramane fara text.
function deepMergeCopy(defaults, override) {
  // absent / gol → default
  if (override == null) return defaults;
  if (typeof override === 'string' && override.trim() === '') return defaults;

  // tipuri diferite → override wins (dar doar daca nu e gol, verificat mai sus)
  if (typeof override !== 'object' || typeof defaults !== 'object') return override;

  // arrays: folosim override daca are cel putin un element, altfel default
  if (Array.isArray(defaults) || Array.isArray(override)) {
    return Array.isArray(override) && override.length > 0 ? override : defaults;
  }

  const out = { ...defaults };
  for (const k of Object.keys(override)) {
    out[k] = deepMergeCopy(defaults[k], override[k]);
  }
  return out;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts || !parsed.value) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      return { stale: true, value: parsed.value };
    }
    return { stale: false, value: parsed.value };
  } catch {
    return null;
  }
}

function writeCache(value) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), value }));
  } catch {
    // quota sau private mode — ignoram
  }
}

/**
 * Hook: intoarce { copy, loading } unde copy este garantat un obiect valid
 * (fie din backend, fie din cache, fie din defaults hardcodate).
 */
export function usePremiumCopy() {
  const cached = readCache();
  const initial = cached?.value
    ? deepMergeCopy(DEFAULT_PREMIUM_COPY, cached.value)
    : DEFAULT_PREMIUM_COPY;
  const [copy, setCopy] = useState(initial);
  const [loading, setLoading] = useState(!cached || cached.stale);

  useEffect(() => {
    // Daca avem cache fresh, nu mai facem call.
    if (cached && !cached.stale) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const apiBase = getApiBaseUrl();
    fetch(`${apiBase}/api/config/premium-copy`)
      .then(res => res.ok ? res.json() : Promise.reject(res.status))
      .then(data => {
        if (cancelled) return;
        // Merge peste defaults ca sa nu lipseasca nicio cheie, chiar daca backend-ul
        // intoarce un shape mai vechi (ex: DB-ul inca are trial_badge).
        const merged = deepMergeCopy(DEFAULT_PREMIUM_COPY, data);
        setCopy(merged);
        writeCache(merged);
      })
      .catch(err => {
        console.warn('[premium-copy] fetch failed, folosesc fallback:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // cached ramane stabil per mount — nu il includem ca sa nu reintrăm
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { copy, loading };
}
